/*
  User facing transaction type label

  User can edit
*/
import { useState, useEffect } from 'react'

import { updateUserTransaction } from '@/features/transactions/transactions.slice'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'
import { Select } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsLockedTransaction } from '@/features/transactions/transactions.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'
import { selectIsInUnservicedFreeTrial } from '@/selectors/user.selectors'
import { TRANSACTION_TYPE_OPTIONS } from '@/constants/transactionConstants'

interface EditableTransactionTypeLabelProps {
  transaction: TransactionOrSplit
}

export const EditableTransactionTypeLabel = ({
  transaction,
}: EditableTransactionTypeLabelProps) => {
  const dispatch = useAppDispatch()
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)
  const locked = useReselector(
    selectIsLockedTransaction,
    transaction.splitFrom || transaction.id
  )
  const [transactionType, setTransactionType] = useState<
    'personal' | 'business' | 'split'
  >()
  const [updating, setUpdating] = useState(false)
  useEffect(() => {
    setTransactionType(transaction.type || undefined)
  }, [transaction])

  const updateTransactionType = async (
    value: TransactionRuleCategoryType | undefined
  ) => {
    await setTransactionType(value)
    await setUpdating(true)
    const updated = await updateUserTransaction(transaction.id, {
      type: value,
      excludedAt: value === 'personal' ? Date.now() : null,
    })(dispatch)
    if (updated) {
      await setUpdating(false)
    }
  }

  if (isInUnservicedFreeTrial) {
    return (
      <Select
        variant="text"
        placeholder="Select type"
        value={transactionType}
        options={TRANSACTION_TYPE_OPTIONS}
        onValueChange={(value) =>
          updateTransactionType(value as TransactionRuleCategoryType)
        }
        disabled={updating || locked}
        withTrigger
      />
    )
  }

  return (
    <div
      className={`w-full [&_button>span:last-child]:hidden ${locked ? '[&_button]:border-transparent [&_button]:bg-transparent [&_button]:disabled:bg-transparent [&_button]:disabled:text-gray-500' : ''}`}
    >
      <Select
        value={transactionType}
        variant="default"
        options={TRANSACTION_TYPE_OPTIONS}
        placeholder="Select..."
        onValueChange={(value) =>
          updateTransactionType(value as TransactionRuleCategoryType)
        }
        disabled={updating || locked}
      />
    </div>
  )
}
